import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, User, Send, MessageCircle, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import PrivacyPolicy from '../components/PrivacyPolicy';

// Define your single, clean Hostinger PHP endpoint at the top
const GOOGLE_APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwzXcbDnr2Z54zR2l15gU04VqUT01RdDYeOtd_6ba2kvFuzNBd1IjYy7yJpczzvzra2/exec";

// Sticky Sidebar Form - Fixed on right side (Desktop only)
export function StickySidebarForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error("Please fill all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const now = new Date();
      const formPayload = new FormData();

      formPayload.append(
        "timestamp",
        now.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(',', '') + '+0530'
      );
      
      formPayload.append("name", formData.name);
      formPayload.append(
        "phone", 
        formData.phone.startsWith("+91") || formData.phone.startsWith("91") ? "'" + formData.phone : "'+91" + formData.phone
      );

      const response = await fetch(
        GOOGLE_APPS_SCRIPT_URL,
        {
          method: "POST",
          body: formPayload,
        }
      );

      if (!response.ok) {
        throw new Error("Server communication failed.");
      }

      setFormData({
        name: "",
        phone: "",
      });
      navigate('/thank-you', { state: { leadSubmitted: true } });

    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }

    setIsSubmitting(false);
  };

  return (
    <div className="hidden lg:block fixed right-0 top-20 w-72 xl:w-80 h-[calc(100vh-80px)] bg-white shadow-2xl z-40 overflow-y-auto border-l border-gray-100">
      <div className="p-4 xl:p-5 space-y-4">
        {/* Header */}
        <h3 className="text-base xl:text-lg font-bold text-gray-900 mt-8">
          Pre-Register here for <span className="text-[#9A7B4F]">Best Offers</span>
        </h3>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="text"
            placeholder="Enter Your Name here..."
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border-gray-300 rounded-none py-3 xl:py-4 text-gray-700 placeholder:text-gray-400 text-sm"
            data-testid="sidebar-name-input"
          />
          <div className="flex items-center border border-gray-300">
            <div className="px-2 py-2 bg-gray-50 border-r border-gray-300 flex items-center gap-1">
              <span className="text-sm">🇮🇳</span>
              <span className="text-xs text-gray-600">+91</span>
            </div>
            <Input
              type="tel"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="flex-1 border-0 rounded-none py-3 xl:py-4 text-gray-700 placeholder:text-gray-400 text-sm focus-visible:ring-0"
              data-testid="sidebar-phone-input"
            />
          </div>

          {/* Consent */}
          <p className="text-[8px] xl:text-[9px] text-gray-400 leading-tight">
            <Check className="w-3 h-3 inline text-[#9A7B4F] mr-1" />
            I Consent to The Processing of Provided Data According To <span className="text-[#9A7B4F] underline">Privacy Policy</span>. I Authorize SAI SUN CITY and its representatives to Call, SMS, Email or WhatsApp Me.
          </p>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#9A7B4F] hover:bg-[#8A6B3F] text-white rounded-none py-4 xl:py-5 text-sm font-semibold"
            data-testid="sidebar-submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Pre-Register Now'}
          </Button>
        </form>

        {/* Features */}
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
          <div className="text-center">
            <div className="w-9 h-9 xl:w-10 xl:h-10 mx-auto mb-1 bg-[#E0F2F1] rounded-full flex items-center justify-center">
              <Phone className="w-4 h-4 text-[#26A69A]" />
            </div>
            <p className="text-[9px] xl:text-[10px] text-gray-600 leading-tight">Instant<br />Call Back</p>
          </div>
          <div className="text-center">
            <div className="w-9 h-9 xl:w-10 xl:h-10 mx-auto mb-1 bg-[#E0F2F1] rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-[#26A69A]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
              </svg>
            </div>
            <p className="text-[9px] xl:text-[10px] text-gray-600 leading-tight">Free Site<br />Visit</p>
          </div>
          <div className="text-center">
            <div className="w-9 h-9 xl:w-10 xl:h-10 mx-auto mb-1 bg-[#E0F2F1] rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-[#26A69A]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <p className="text-[9px] xl:text-[10px] text-gray-600 leading-tight">Best<br />Price</p>
          </div>
        </div>

        {/* Request Call Back Button */}
        <Button
          onClick={() => window.open('tel:+919321795663')}
          className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white rounded-none py-4 font-semibold text-sm"
        >
          Request Call Back
        </Button>
      </div>
    </div>
  );
}

// Popup Form Component
export function PopupForm({ isOpen, onClose }) {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({ 
    name: '', 
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  // Final Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error("Please fill all contact fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const now = new Date();
      const formPayload = new FormData();

      formPayload.append(
        "timestamp",
        now.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(',', '') + '+0530'
      );
      
      formPayload.append("name", formData.name);
      formPayload.append(
        "phone", 
        formData.phone.startsWith("+91") || formData.phone.startsWith("91") ? "'" + formData.phone : "'+91" + formData.phone
      );

      const response = await fetch(
        GOOGLE_APPS_SCRIPT_URL,
        {
          method: "POST",
          body: formPayload,
        }
      );

      if (!response.ok) {
        throw new Error("Server communication failed.");
      }

      // Complete cleanup reset
      setFormData({
        name: "",
        phone: "",
      });
      // setStep(1); 
      onClose();
      navigate('/thank-you', { state: { leadSubmitted: true } });

    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }

    setIsSubmitting(false);
  };

  const handleCloseWrapper = () => {
    // setStep(1);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseWrapper}>
      <DialogContent 
        className="bg-white border-gray-200 w-[92vw] max-w-sm sm:max-w-md p-0 overflow-hidden rounded-md sm:rounded-lg max-h-[85vh] flex flex-col" 
        data-testid="popup-form"
      >
        {/* Fixed Header Section */}
        <div className="bg-[#9A7B4F] text-white p-4 sm:p-5 shrink-0">
          <DialogTitle className="font-serif text-base sm:text-xl font-bold text-center leading-snug">
            Get a Callback
          </DialogTitle>

          <p className="text-white/90 text-center text-xs sm:text-sm mt-1 leading-relaxed">
            Enter your details and we'll call you back within 5 minutes
          </p>
        </div>
        
        {/* Scrollable Form Body Container */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900 block">
                Your Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A7B4F] pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Enter Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-300 rounded-none focus:border-[#9A7B4F] focus:ring-[#9A7B4F] text-gray-900 placeholder:text-gray-400 py-3.5 sm:py-5 text-xs sm:text-sm font-medium"
                  data-testid="popup-name-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900 block">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A7B4F] pointer-events-none" />
                <Input
                  type="tel"
                  placeholder="Enter Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-300 rounded-none focus:border-[#9A7B4F] focus:ring-[#9A7B4F] text-gray-900 placeholder:text-gray-400 py-3.5 sm:py-5 text-xs sm:text-sm font-medium"
                  data-testid="popup-phone-input"
                />
              </div>
            </div>
            
            <div className="flex justify-center pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-2/3 bg-[#9A7B4F] text-white hover:bg-[#8A6B3F] active:bg-[#8A6B3F] rounded-none py-3.5 sm:py-5 font-bold text-xs sm:text-sm tracking-wider uppercase transition-colors"
                data-testid="popup-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Sticky Side Form Button - Mobile Only
export function StickySideForm({ onOpen }) {
  return (
    <motion.button
      initial={{ x: 100 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.5, delay: 1 }}
      onClick={onOpen}
      className="lg:hidden fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-[#9A7B4F] hover:bg-[#8A6B3F] transition-colors py-3 px-1.5 shadow-lg"
      data-testid="sticky-side-form-btn"
    >
      <span className="sticky-form-btn text-white font-semibold text-xs tracking-wider">
        ENQUIRE
      </span>
    </motion.button>
  );
}

// Footer Contact Form Component
export function FooterContactForm() {
  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="py-10 bg-[#E8ECEF] text-gray-800 text-center font-sans"
    >
      <div className="max-w-6xl mx-auto px-4 text-xs sm:text-sm leading-relaxed space-y-4">
        
        {/* RERA Registration Row */}
        <p className="font-semibold text-gray-900">
          This project is RERA registered. | Channel Partner RERA Number :{' '}
          <span className="font-normal text-gray-700">A011272502396</span> |{' '}
          Project RERA Number :{' '}
          <span className="font-normal text-gray-700">P52000033880, P52000045371, P52000046738</span>
        </p>

        {/* Legal Disclaimer Text block */}
        <p className="text-gray-600 max-w-5xl mx-auto text-[11px] sm:text-xs">
          <strong className="text-gray-900">Disclaimer :</strong> Please be advised that this website is not an official site and serves solely as an informational portal managed by a RERA authorized real estate agent. It does not constitute an offer or guarantee of any services. The prices displayed on this website are subject to change without prior notice, and the availability of properties cannot be guaranteed. The images showcased on this website are for representational purposes only and may not accurately reflect the actual properties. We may share your data with Real Estate Regulatory Authority (RERA) registered Developers for further processing as necessary. Additionally, we may send updates and information to the mobile number or email address registered with us. All rights reserved. The content, design, and information on this website are protected by copyright and other intellectual property rights. Any unauthorized use or reproduction of the content may violate applicable laws. For accurate and up-to-date information regarding services, pricing, availability, and any other details, it is recommended to contact us directly through the provided contact information on this website. Thank you for visiting our website.
        </p>

        {/* Corporate Branding & Copyright */}
        <p className="font-bold text-gray-900 pt-2 text-xs sm:text-sm">
          All Rights Reserved. © {new Date().getFullYear()} | saisuncitykharghar.in
        </p>

        {/* Compliance Hyperlink Bar */}
        <div className="flex justify-center items-center gap-2 text-[#007BFF] text-xs font-medium pt-1">
          <span className="hover:underline cursor-pointer">
          <a href="/disclaimer" target="_blank" rel="noopener noreferrer">
            Disclaimer
          </a>
          </span>
          <span className="text-gray-400">|</span>
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            Privacy Policy
          </a>
          <span className="text-gray-400">|</span>
          <a
            href="/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            Terms & Conditions
          </a>
        </div>

      </div>
    </footer>
  );
}

// WhatsApp Floating Button
export function WhatsAppButton() {
  return (
    <motion.a
      href="https://wa.me/919321795663?text=Hi%2C%20I%27m%20interested%20in%20Sai%20Sun%20City.%20Please%20share%20more%20details."
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 1.5 }}
      className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-[300px] xl:right-[340px] z-50 w-12 h-12 sm:w-14 sm:h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-colors whatsapp-pulse"
      data-testid="whatsapp-floating-btn"
    >
      <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </motion.a>
  );
}